import { AsyncPipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  map,
  startWith,
} from 'rxjs';

export interface SearchableSelectOption<T = number | string> {
  value: T;
  label: string;
}

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Input() label = '';
  @Input() formFieldClass = 'w-full';
  @Input() requireSelection = false;
  @Input() disabled = false;
  @Input() items: SearchableSelectOption[] = [];

  @ViewChild('auto') private autocomplete?: MatAutocomplete;

  control = new FormControl<number | string | null>(null);
  filteredOptions$!: Observable<SearchableSelectOption[]>;

  private readonly searchText$ = new BehaviorSubject('');
  private cvaDisabled = false;
  private onChange: (value: number | string | null) => void = () => {};

  ngOnInit(): void {
    this.filteredOptions$ = combineLatest([
      this.control.valueChanges.pipe(startWith(this.control.value)),
      this.searchText$,
    ]).pipe(
      map(([controlValue, searchText]) => this.filter(controlValue, searchText))
    );

    this.control.valueChanges.subscribe(value => {
      this.onChange(typeof value === 'string' ? null : value);
    });

    this.updateDisabledState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['disabled']) {
      this.updateDisabledState();
    }
  }

  get isReadonly(): boolean {
    return this.items.length === 0;
  }

  get isDisabled(): boolean {
    return this.disabled || this.cvaDisabled;
  }

  get isAutocompleteDisabled(): boolean {
    return this.isDisabled || this.isReadonly;
  }

  onInput(event: Event): void {
    this.searchText$.next((event.target as HTMLInputElement).value);
  }

  onOptionSelected(): void {
    this.searchText$.next('');
  }

  onAutocompleteOpened(): void {
    const current = this.control.value;
    if (typeof current === 'string') {
      return;
    }

    queueMicrotask(() => {
      this.autocomplete?.options.forEach(option => {
        if (option.value === current) {
          option.select(false);
        } else if (option.selected) {
          option.deselect(false);
        }
      });
    });
  }

  displayLabel = (value: number | string | null): string => {
    if (typeof value === 'string') {
      return value;
    }

    const option = this.items.find(item => item.value === value);
    return option?.label ?? '';
  };

  writeValue(value: number | string | null): void {
    const current = this.control.value;

    if (typeof current === 'string' && current !== '') {
      return;
    }

    this.control.setValue(value, { emitEvent: false });
    this.searchText$.next('');
  }

  registerOnChange(fn: (value: number | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(): void {}

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
    this.updateDisabledState();
  }

  private updateDisabledState(): void {
    if (this.isDisabled) {
      this.control.disable({ emitEvent: false });
      return;
    }

    this.control.enable({ emitEvent: false });
  }

  private filter(
    controlValue: string | number | null,
    searchText: string
  ): SearchableSelectOption[] {
    const query =
      searchText || (typeof controlValue === 'string' ? controlValue : '');

    if (!query) {
      return this.items;
    }

    const filterValue = query.toLowerCase();

    return this.items.filter(option =>
      option.label.toLowerCase().includes(filterValue)
    );
  }
}
